import { useDirectMarkerUpdates } from 'components/minute/builders/v2/useDirectMarkerUpdates';
import { IconMarkerStyleBackgroundSubsection } from 'components/minute/sidebar/MarkerSidebar/IconMarkerSiderbar/IconMarkerStyleSection/IconMarkerStyleBackgroundSubsection';
import { IconMarkerStyleColorSubsection } from 'components/minute/sidebar/MarkerSidebar/IconMarkerSiderbar/IconMarkerStyleSection/IconMarkerStyleColorSubsection';
import { IconMarkerStyleSizeSubsection } from 'components/minute/sidebar/MarkerSidebar/IconMarkerSiderbar/IconMarkerStyleSection/IconMarkerStyleSizeSubsection';
import { ImageGalleriesSubsection } from 'components/minute/sidebar/MarkerSidebar/shared/ImageGalleriesSubsection';
import { TRANSPARENT_COLOR } from 'elements/minute/sidebar/sidebarComponents/complex/ColorPalette';
import { SidebarSection } from 'elements/minute/sidebar/sidebarSection/SidebarSection';
import { SidebarSubsection } from 'elements/minute/sidebar/sidebarSection/SidebarSubsection';
import { useIconPreview } from 'hooks/marker/useIconPreview';
import { useWorldMarker } from 'hooks/marker/useWorldMarker';
import { useContext } from 'react';
import { IIconV2WorldMarker } from 'react-give-me-map';
import { CtxEdit } from 'session/ctxEdit';
import { Localization } from 'static/localization/localization';
import styled from 'styled-components/macro';

interface Props {
    className?: string;
    marker: IIconV2WorldMarker;
}

export const IconMarkerStyleSection = (props: Props): JSX.Element => {
    const ctxEdit = useContext(CtxEdit);
    const worldMarker = useWorldMarker();
    const directMarkerUpdates = useDirectMarkerUpdates();
    const selectedMarker = worldMarker.getSingleMarkerIfOne() as IIconV2WorldMarker;

    const iconPreview = useIconPreview({
        mapRef: ctxEdit.mapRef,
        worldMarkers: ctxEdit.worldMarkers,
        markerIds: [props.marker.id],
        initialData: props.marker.elementData,
        markerType: 'icon',
        onNewImageGenerated: ({}) => {
            const newMarker: IIconV2WorldMarker = {
                ...props.marker,
                elementData: {
                    ...props.marker.elementData,
                    imageUrl: `temp-icon-${props.marker.id}`
                }
            };
            directMarkerUpdates.updateIconLikeMarker('v2/icon', newMarker);

            ctxEdit.updateWorldMarkerData(props.marker.id, {
                imageUrl: `temp-icon-${props.marker.id}`
            });
        }
    });

    return (
        <S_IconMarkerStyleSection className={props.className}>
            <SidebarSection title={Localization.STYLE} icon={{ iconName: 'brush' }}>
                <SidebarSubsection title={Localization.ICON} icon={{ iconName: 'image' }}>
                    <ImageGalleriesSubsection
                        marker={props.marker}
                        galleryType="icon"
                        order={['icon', 'image']}
                        allowEmpty
                        onSelected={(data) => {
                            if (data.type === 'symbol') {
                                const color = data.color
                                    ? data.color
                                    : props.marker.elementData.generatedIconColor;
                                iconPreview.setIcon(data.imageUrl, color);

                                if (data.imageUrl) {
                                    ctxEdit.updateWorldMarkerData(props.marker.id, {
                                        generatedIconUrl: data.imageUrl,
                                        generatedImageUrl: null,
                                        generatedEmoji: null,
                                        imageUrl: `temp-icon-${props.marker.id}`
                                    });
                                    ctxEdit.setMarkerDefault({
                                        ...ctxEdit.markerDefault,
                                        pin: {
                                            ...ctxEdit.markerDefault.pin,
                                            generatedIconUrl: data.imageUrl
                                        }
                                    });
                                } else {
                                    ctxEdit.updateWorldMarkerData(props.marker.id, {
                                        generatedIconUrl: null,
                                        generatedImageUrl: null,
                                        generatedEmoji: null,
                                        generatedBackgroundColor: TRANSPARENT_COLOR,
                                        imageUrl: null
                                    });
                                }
                            }
                            if (data.type === 'image') {
                                iconPreview.setImage(data.imageUrl);
                                ctxEdit.updateWorldMarkerData(props.marker.id, {
                                    generatedImageUrl: data.imageUrl,
                                    generatedIconUrl: null,
                                    generatedEmoji: null,
                                    imageUrl: `temp-icon-${props.marker.id}`
                                });
                            }
                        }}
                    />
                </SidebarSubsection>
                {selectedMarker?.elementData.generatedIconUrl && (
                    <IconMarkerStyleColorSubsection
                        marker={props.marker}
                        onColorChanged={(color) => {
                            iconPreview.setIconColor(color);
                            ctxEdit.updateWorldMarkerData(props.marker.id, {
                                generatedIconColor: color
                            });
                            ctxEdit.setMarkerDefault({
                                ...ctxEdit.markerDefault,
                                pin: {
                                    ...ctxEdit.markerDefault.pin,
                                    generatedIconColor: color
                                }
                            });
                        }}
                    />
                )}
                <IconMarkerStyleBackgroundSubsection
                    marker={props.marker}
                    onBackgroundChanged={(color) => {
                        iconPreview.setBackgroundColor(color);
                        ctxEdit.updateWorldMarkerData(props.marker.id, {
                            generatedBackgroundColor: color
                        });
                        ctxEdit.setMarkerDefault({
                            ...ctxEdit.markerDefault,
                            pin: {
                                ...ctxEdit.markerDefault.pin,
                                generatedBackgroundColor: color
                            }
                        });
                    }}
                />
                <S_Canvas ref={iconPreview.canvasRef} width={200} height={200} />
                <IconMarkerStyleSizeSubsection marker={props.marker} />
            </SidebarSection>
        </S_IconMarkerStyleSection>
    );
};

const S_IconMarkerStyleSection = styled.div``;

const S_Canvas = styled.canvas`
    width: 100px;
    height: 100px;
    display: none;
`;
